import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RefreshCw } from "lucide-react";

type DebugMode = "processed" | "stats" | "locations";

const LABELS: Record<DebugMode, string> = {
  processed: "Fetch Processed Response",
  stats: "Fetch Raw Vehicle Stats",
  locations: "Fetch Raw Vehicle Locations",
};

export function SamsaraDebugTab() {
  const [response, setResponse] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState<DebugMode | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [activeMode, setActiveMode] = useState<DebugMode | null>(null);

  const fetchSamsara = async (mode: DebugMode) => {
    setIsLoading(mode);
    try {
      const queryParams = mode !== "processed" ? `?debug=${mode}` : "";
      const { data, error } = await supabase.functions.invoke(
        `fetch-samsara-vehicles${queryParams}`
      );
      if (error) {
        setResponse({ error: error.message });
      } else {
        setResponse(data);
      }
      setFetchedAt(new Date().toLocaleTimeString());
      setActiveMode(mode);
    } catch (err) {
      setResponse({ error: err instanceof Error ? err.message : "Unknown error" });
      setFetchedAt(new Date().toLocaleTimeString());
      setActiveMode(mode);
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Samsara API Debug</CardTitle>
        <CardDescription>
          Fetch and inspect raw or processed JSON responses from the Samsara vehicles endpoint.
          {fetchedAt && <span className="ml-2 text-xs">Last fetched: {fetchedAt}</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(LABELS) as DebugMode[]).map((mode) => (
            <Button
              key={mode}
              onClick={() => fetchSamsara(mode)}
              disabled={isLoading !== null}
              variant={activeMode === mode ? "default" : "outline"}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading === mode ? "animate-spin" : ""}`} />
              {isLoading === mode ? "Fetching..." : LABELS[mode]}
            </Button>
          ))}
        </div>
        {response && (
          <ScrollArea className="h-[600px] rounded-md border bg-muted p-4">
            <pre className="text-xs font-mono whitespace-pre-wrap break-all">
              {JSON.stringify(response, null, 2)}
            </pre>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
